export const svgEl = (name: string, attributes: any = {}) => {
    const element = document.createElementNS(
        'http://www.w3.org/2000/svg',
        name
    );

    for (const attr in attributes || {}) {
        if (Object.prototype.hasOwnProperty.call(attributes, attr)) {
            element.setAttribute(attr, attributes[attr]);
        }
    }
    return element;
};

export const svgShadowGroup = () => {
    const group = svgEl('g'),
        filter = svgEl('filter', {id: 'blur'}),
        blur = svgEl('feGaussianBlur', {
            in: 'SourceGraphic',
            stdDeviation: '2',
        });

    filter.appendChild(blur);
    group.appendChild(filter);
    return group;
};

export const svgShadow = (x: number, y: number) => {
    svgEl('circle', {
        cx: x,
        cy: y,
        r: 43,
        stroke: 'none',
        fill: 'black',
        opacity: 0.32,
        filter: 'url(#blur)',
    });
};

export const svgStone = (
    x: number,
    y: number,
    color = 'black',
    r = 42,
    analysis = true
) => {
    let className = 'gp-svg-grey-stone'; // Grey stone by default

    if (color === 'black' || color === '#000000') {
        className = 'gp-svg-black-stone';
    } else if (color === 'white' || color === '#ffffff') {
        className = 'gp-svg-white-stone';
    } else {
        className = 'gp-svg-grey-stone';
    }

    if (analysis) className += ' gp-svg-analysis';

    return svgEl('circle', {
        cx: x,
        cy: y,
        r,
        class: className,
    });
};

export const svgCircle = (
    x: number,
    y: number,
    fill: string,
    r = 42,
    strokeColor = 'none',
    strokeWidth = 0
) => {
    return svgEl('circle', {
        cx: x,
        cy: y,
        r,
        fill,
        stroke: strokeColor,
        'stroke-width': strokeWidth,
    });
};

export const svgRing = (
    x: number,
    y: number,
    strokeColor: string,
    size: number,
    strokeWidth = 3
) => {
    const r = size / 2 + 4;
    return svgEl('circle', {
        cx: x,
        cy: y,
        r,
        fill: 'none',
        stroke: strokeColor,
        'stroke-width': strokeWidth,
    });
};

// Makes a label at (x, y)
export const svgLabel = (
    x: number,
    y: number,
    color: string,
    label: string,
    truncation = true
) => {
    let size;

    // Trims label to 3 characters
    if (truncation && label.length >= 3) {
        label = label.slice(0, 2) + '…';
    }

    // Set font size according to label length
    switch (label.length) {
        case 1:
            size = 72;
            break;
        case 2:
            size = 56;
            break;
        case 3:
            size = 36;
            break;
        // 4-6 characters are usually used for numbers
        case 4:
            size = 34;
            break;
        case 5:
            size = 32;
            break;
        case 6:
            size = 30;
    }

    const element = svgEl('text', {
        x: x,
        y: y,
        dy: '.65ex', // Seems to work for vertically centering these fonts
        'font-size': size,
        'text-anchor': 'middle', // Horizontal centering
        'font-family': 'Helvetica, Arial, sans-serif',
        fill: color,
    });
    element.appendChild(document.createTextNode(label));

    return element;
};

export const svgLine = (
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    color = 'black',
    width = 2,
    dashArray = ''
) => {
    return svgEl('line', {
        x1,
        y1,
        x2,
        y2,
        stroke: color,
        'stroke-width': width,
        'stroke-dasharray': dashArray,
    });
};

export const svgDoubleLine = (
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    color = 'black',
    width = 2,
    gap = 4,
    dashArray = ''
) => {
    const group = svgEl('g');

    const dx = x2 - x1;
    const dy = y2 - y1;

    // If the line is a point, return the empty group
    if (dx === 0 && dy === 0) {
        return group;
    }

    const length = Math.hypot(dx, dy);
    const offsetX = (gap * dy) / length / 2;
    const offsetY = (gap * -dx) / length / 2;

    const line1 = svgEl('line', {
        x1: x1 + offsetX,
        y1: y1 + offsetY,
        x2: x2 + offsetX,
        y2: y2 + offsetY,
        stroke: color,
        'stroke-width': width,
        'stroke-dasharray': dashArray,
    });

    const line2 = svgEl('line', {
        x1: x1 - offsetX,
        y1: y1 - offsetY,
        x2: x2 - offsetX,
        y2: y2 - offsetY,
        stroke: color,
        'stroke-width': width,
        'stroke-dasharray': dashArray,
    });

    group.appendChild(line1);
    group.appendChild(line2);

    return group;
};

export const svgEquilateralTriangle = (
    cx: number,
    cy: number,
    size: number,
    rotation = 0,
    color = 'black',
    fill = 'none',
    width = 2,
    dashArray = ''
) => {
    const height = (Math.sqrt(3) / 2) * size; // Height of an equilateral triangle
    const halfSize = size / 2;

    // Define the three vertices relative to (cx, cy)
    const x1 = cx,
        y1 = cy - height / 2; // Top vertex
    const x2 = cx - halfSize,
        y2 = cy + height / 2; // Bottom-left vertex
    const x3 = cx + halfSize,
        y3 = cy + height / 2; // Bottom-right vertex

    // Function to rotate a point (px, py) around (cx, cy)
    const rotatePoint = (px: number, py: number, angle: number) => {
        const rad = (angle * Math.PI) / 180;
        const cos = Math.cos(rad);
        const sin = Math.sin(rad);
        const dx = px - cx;
        const dy = py - cy;
        return {
            x: cx + dx * cos - dy * sin,
            y: cy + dx * sin + dy * cos,
        };
    };

    // Apply rotation to each point
    const p1 = rotatePoint(x1, y1, rotation);
    const p2 = rotatePoint(x2, y2, rotation);
    const p3 = rotatePoint(x3, y3, rotation);

    return svgEl('polygon', {
        points: `${p1.x},${p1.y} ${p2.x},${p2.y} ${p3.x},${p3.y}`,
        stroke: color,
        'stroke-width': width,
        'stroke-dasharray': dashArray,
        fill,
    });
};

export const svgSquare = (
    x: number,
    y: number,
    size: number,
    color = 'black',
    fill = 'none',
    width = 2,
    dashArray = ''
) => {
    return svgEl('rect', {
        x: x - size / 2, // Centering the square
        y: y - size / 2,
        width: size,
        height: size,
        stroke: color,
        'stroke-width': width,
        'stroke-dasharray': dashArray,
        fill,
    });
};

export const svgIcon = (
    x: number,
    y: number,
    icon: string,
    size: number,
    color = 'black',
    scale = 0.25
) => {
    const wrapper = svgEl('g');

    const parser = new DOMParser();
    const svgElement = parser.parseFromString(
        icon,
        'image/svg+xml'
    ).documentElement;

    wrapper.innerHTML = svgElement.innerHTML;
    wrapper.setAttribute(
        'transform',
        `translate(${x - size / scale}, ${y - size / scale}) scale(${(size / 16) * scale})`
    );

    const elements = wrapper.querySelectorAll('*');
    elements.forEach(el => {
        el.setAttribute('fill', color);
    });

    return wrapper;
};
